/**
 * ============================================================================
 * RAGE NATION - PREMIUM QUICK VIEW MODAL
 * ============================================================================
 * Handles all quick view modal functionality including:
 * - Modal open/close with smooth animations
 * - Image carousel navigation
 * - Variant selection and price updates
 * - Quantity controls
 * - Add to cart with loading states
 * ============================================================================
 */

class RNQuickView {
  constructor() {
    this.modal = null;
    this.backdrop = null;
    this.variants = [];
    this.currentVariant = null;
    this.currentImageIndex = 0;
    this.isOpen = false;
    this.isLoading = false;
    this.touchStartY = 0;

    this.init();
  }

  init() {
    // Bind methods
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);

    // Set up event delegation for quick add buttons
    document.addEventListener('click', (e) => {
      const quickAddBtn = e.target.closest('[data-rn-quick-view-trigger]');
      if (quickAddBtn) {
        e.preventDefault();
        const productHandle = quickAddBtn.dataset.productHandle;
        if (productHandle) {
          this.loadAndOpen(productHandle);
        }
      }
    });
  }

  /**
   * Load product data and open modal
   */
  async loadAndOpen(productHandle) {
    if (this.isLoading) return;
    this.isLoading = true;

    try {
      // Fetch the product section
      const response = await fetch(`/products/${productHandle}?section_id=rn-quick-view-data`);
      if (!response.ok) throw new Error('Failed to load product');

      const html = await response.text();

      // Create temporary container
      const temp = document.createElement('div');
      temp.innerHTML = html;

      // Find or create the quick view container
      let container = document.getElementById('rn-quick-view-container');
      if (!container) {
        container = document.createElement('div');
        container.id = 'rn-quick-view-container';
        document.body.appendChild(container);
      }

      // Insert the modal HTML
      container.innerHTML = temp.innerHTML;

      // Initialize the modal
      this.setupModal();
      this.open();

    } catch (error) {
      console.error('RN Quick View Error:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Set up modal elements and event listeners
   */
  setupModal() {
    this.modal = document.querySelector('[data-rn-quick-view]');
    this.backdrop = document.querySelector('[data-rn-qv-backdrop]');

    // Get the container that holds both modal and variant data
    const container = document.getElementById('rn-quick-view-container');

    if (!this.modal) {
      console.error('[RN Quick View] Modal element not found!');
      return;
    }

    // Load variant data - search in container since script tag is sibling to modal
    const variantData = container?.querySelector('[data-rn-qv-variants]')
      || document.querySelector('[data-rn-qv-variants]');

    if (variantData) {
      try {
        this.variants = JSON.parse(variantData.textContent);

        // Get initial variant from hidden input (set by Liquid)
        const variantInput = this.modal.querySelector('[data-rn-qv-variant-id]');
        const initialVariantId = variantInput ? parseInt(variantInput.value) : null;

        // Find the initial variant, fallback to first available
        this.currentVariant = this.variants.find(v => v.id === initialVariantId)
          || this.variants.find(v => v.available)
          || this.variants[0];

        console.log('[RN Quick View] ✓ Loaded', this.variants.length, 'variants');
        console.log('[RN Quick View] Initial variant:', this.currentVariant?.id, this.currentVariant?.title, this.currentVariant?.options);
      } catch (e) {
        console.error('[RN Quick View] Failed to parse variants:', e);
      }
    } else {
      console.error('[RN Quick View] Variant data script tag not found!');
    }

    // Load backorder/pre-order data for Fabricator integration
    const backorderData = container?.querySelector('[data-rn-qv-backorder]')
      || document.querySelector('[data-rn-qv-backorder]');

    if (backorderData) {
      try {
        const parsed = JSON.parse(backorderData.textContent);
        this.backorderVariants = parsed.backorder_variants || [];
        console.log('[RN Quick View] ✓ Loaded', this.backorderVariants.length, 'backorder variants');
      } catch (e) {
        console.error('[RN Quick View] Failed to parse backorder data:', e);
        this.backorderVariants = [];
      }
    } else {
      this.backorderVariants = [];
    }

    // Close button
    const closeBtn = this.modal.querySelector('[data-rn-qv-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', this.close);
    }

    // Backdrop click
    if (this.backdrop) {
      this.backdrop.addEventListener('click', this.close);
    }

    // Image navigation
    this.setupImageCarousel();

    // Option buttons (color/size)
    this.setupOptionButtons();

    // Quantity controls
    this.setupQuantityControls();

    // Form submission
    this.setupFormSubmission();

    // Touch gestures for mobile — both passive: handler never calls preventDefault,
    // and passive listeners let the compositor scroll without waiting on JS (fixes scroll jank).
    this.modal.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.modal.addEventListener('touchmove', this.handleTouchMove, { passive: true });

    // Keyboard navigation
    document.addEventListener('keydown', this.handleKeydown);
  }

  /**
   * Image carousel functionality
   */
  setupImageCarousel() {
    const slides = this.modal.querySelectorAll('[data-rn-qv-slide]');
    const dots = this.modal.querySelectorAll('[data-rn-qv-dot]');
    const prevBtn = this.modal.querySelector('[data-rn-qv-prev]');
    const nextBtn = this.modal.querySelector('[data-rn-qv-next]');

    if (slides.length <= 1) return;

    const goToSlide = (index) => {
      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;

      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
      });

      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });

      this.currentImageIndex = index;
    };

    // Arrow navigation
    if (prevBtn) prevBtn.addEventListener('click', () => goToSlide(this.currentImageIndex - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goToSlide(this.currentImageIndex + 1));

    // Dot navigation
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => goToSlide(index));
    });

    // Swipe support for mobile
    let touchStartX = 0;
    const imagesContainer = this.modal.querySelector('[data-rn-qv-images]');

    if (imagesContainer) {
      imagesContainer.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
      }, { passive: true });

      imagesContainer.addEventListener('touchend', (e) => {
        const touchEndX = e.changedTouches[0].clientX;
        const diff = touchStartX - touchEndX;

        if (Math.abs(diff) > 50) {
          if (diff > 0) {
            goToSlide(this.currentImageIndex + 1);
          } else {
            goToSlide(this.currentImageIndex - 1);
          }
        }
      }, { passive: true });
    }
  }

  /**
   * Option button selection (color/size)
   */
  setupOptionButtons() {
    const optionButtons = this.modal.querySelectorAll('[data-option-value]');

    optionButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;

        const optionIndex = parseInt(btn.dataset.optionIndex);
        const optionValue = btn.dataset.optionValue;

        console.log('[RN Quick View] Option clicked - index:', optionIndex, 'value:', optionValue);

        // Update selected state - only target buttons with data-option-value (not the group container)
        const optionGroup = btn.closest('.rn-quick-view__option-group');
        if (optionGroup) {
          const siblings = optionGroup.querySelectorAll('[data-option-value]');
          siblings.forEach(sib => sib.classList.remove('is-selected'));
        }
        btn.classList.add('is-selected');

        // Update label
        const label = this.modal.querySelector(`[data-rn-qv-option-value="${optionIndex}"]`);
        if (label) label.textContent = optionValue;

        // Find matching variant
        this.updateVariant();
      });
    });
  }

  /**
   * Find and update current variant based on selected options
   */
  updateVariant() {
    if (!this.variants || this.variants.length === 0) {
      console.error('[RN Quick View] No variants loaded!');
      return;
    }

    // Start with current variant's options as base (ensures all options are included)
    const selectedOptions = this.currentVariant?.options ? [...this.currentVariant.options] : [];

    // Update with currently selected values from UI
    const optionGroups = this.modal.querySelectorAll('.rn-quick-view__option-group[data-option-index]');

    console.log('[RN Quick View] Found', optionGroups.length, 'option groups');

    optionGroups.forEach(group => {
      const index = parseInt(group.dataset.optionIndex);
      const selected = group.querySelector('[data-option-value].is-selected');

      console.log('[RN Quick View] Option group', index, '- selected element:', selected ? selected.dataset.optionValue : 'NONE');

      if (selected) {
        selectedOptions[index] = selected.dataset.optionValue;
      }
    });

    console.log('[RN Quick View] Final selected options:', selectedOptions);
    console.log('[RN Quick View] Sample variant for comparison:', this.variants[0]?.title, this.variants[0]?.options);

    // Find matching variant - ALL options must match
    const matchingVariant = this.variants.find(variant => {
      if (!variant.options) return false;

      // Variant options and selected options must match exactly
      if (variant.options.length !== selectedOptions.length) return false;

      return variant.options.every((variantOpt, idx) => {
        return variantOpt === selectedOptions[idx];
      });
    });

    if (matchingVariant) {
      this.currentVariant = matchingVariant;
      this.updateUI();
      console.log('[RN Quick View] ✓ Found variant:', matchingVariant.id, matchingVariant.title);
    } else {
      console.warn('[RN Quick View] ✗ No variant found for:', selectedOptions);
      console.warn('[RN Quick View] Available variants:');
      this.variants.slice(0, 5).forEach(v => {
        console.warn('  -', v.title, ':', v.options);
      });
    }
  }

  /**
   * Update UI based on current variant
   */
  updateUI() {
    const variant = this.currentVariant;
    if (!variant) return;

    // Update hidden variant ID
    const variantInput = this.modal.querySelector('[data-rn-qv-variant-id]');
    if (variantInput) variantInput.value = variant.id;

    // Update price
    const priceEl = this.modal.querySelector('[data-rn-qv-price]');
    if (priceEl) {
      priceEl.textContent = this.formatMoney(variant.price);
    }

    // Update compare price (use class instead of display:none to prevent layout shift)
    const comparePriceEl = this.modal.querySelector('[data-rn-qv-compare-price]');
    if (comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
        comparePriceEl.classList.remove('is-hidden');
      } else {
        comparePriceEl.classList.add('is-hidden');
      }
    }

    // Update sale badge visibility
    const saleBadge = this.modal.querySelector('.rn-quick-view__badge');
    if (saleBadge) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        saleBadge.classList.remove('is-hidden');
      } else {
        saleBadge.classList.add('is-hidden');
      }
    }

    // Update add to cart button
    const addBtn = this.modal.querySelector('[data-rn-qv-add-btn]');
    const btnText = addBtn?.querySelector('.rn-quick-view__btn-text');

    if (addBtn && btnText) {
      // Check if product is on pre-order template
      const isPreorderTemplate = this.modal.dataset.isPreorderTemplate === 'true';

      // Pre-order: inventory_policy = 'continue' means we can still sell even when out of stock
      const isPreorderActive = isPreorderTemplate && variant.inventory_policy === 'continue';

      // Update preorder data attribute for form submission check
      addBtn.dataset.isPreorder = isPreorderActive ? 'true' : 'false';

      if (variant.available) {
        // In stock
        addBtn.disabled = false;
        addBtn.classList.remove('is-sold-out', 'is-preorder');
        if (isPreorderTemplate) {
          btnText.textContent = 'Pre-Order';
          addBtn.classList.add('is-preorder');
        } else {
          btnText.textContent = 'Add to Bag';
        }
      } else if (isPreorderActive) {
        // Out of stock but pre-order enabled
        addBtn.disabled = false;
        addBtn.classList.remove('is-sold-out');
        addBtn.classList.add('is-preorder');
        btnText.textContent = 'Pre-Order';
      } else {
        // Sold out
        addBtn.disabled = true;
        addBtn.classList.add('is-sold-out');
        addBtn.classList.remove('is-preorder');
        btnText.textContent = 'Sold Out';
      }
    }

    // Update unavailable options
    this.updateOptionAvailability();

    // Update image if variant has featured image
    if (variant.featured_image) {
      const slides = this.modal.querySelectorAll('[data-rn-qv-slide]');
      slides.forEach((slide, index) => {
        const img = slide.querySelector('img');
        if (img && img.src.includes(variant.featured_image.id)) {
          this.goToSlide?.(index);
        }
      });
    }
  }

  /**
   * Update which options are available based on current selection
   */
  updateOptionAvailability() {
    // Get current selected options
    const selectedOptions = [];
    for (let i = 0; i < 3; i++) {
      const selected = this.modal.querySelector(`[data-option-index="${i}"].is-selected`);
      if (selected) selectedOptions[i] = selected.dataset.optionValue;
    }

    // Check each option button's availability
    const optionButtons = this.modal.querySelectorAll('[data-option-value]');

    optionButtons.forEach(btn => {
      const optionIndex = parseInt(btn.dataset.optionIndex);
      const optionValue = btn.dataset.optionValue;

      // Create test options array
      const testOptions = [...selectedOptions];
      testOptions[optionIndex] = optionValue;

      // Check if any variant matches
      const isAvailable = this.variants.some(variant => {
        return variant.available && testOptions.every((opt, idx) => {
          return opt === undefined || variant.options[idx] === opt;
        });
      });

      btn.classList.toggle('is-unavailable', !isAvailable);
      btn.disabled = !isAvailable;
    });
  }

  /**
   * Quantity controls
   */
  setupQuantityControls() {
    const minusBtn = this.modal.querySelector('[data-rn-qv-qty-minus]');
    const plusBtn = this.modal.querySelector('[data-rn-qv-qty-plus]');
    const input = this.modal.querySelector('[data-rn-qv-qty-input]');

    if (!input) return;

    const updateQty = (delta) => {
      let value = parseInt(input.value) || 1;
      value = Math.max(1, Math.min(99, value + delta));
      input.value = value;
    };

    if (minusBtn) minusBtn.addEventListener('click', () => updateQty(-1));
    if (plusBtn) plusBtn.addEventListener('click', () => updateQty(1));

    input.addEventListener('change', () => {
      let value = parseInt(input.value) || 1;
      input.value = Math.max(1, Math.min(99, value));
    });
  }

  /**
   * Form submission (Add to Cart)
   * Matches theme's ProductForm behavior for compatibility with Fabricator and cart drawer
   */
  setupFormSubmission() {
    const form = this.modal.querySelector('[data-rn-qv-form]');
    const addBtn = this.modal.querySelector('[data-rn-qv-add-btn]');

    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // Check if variant can be added:
      // - Regular products: variant.available must be true
      // - Pre-order products: inventory_policy = 'continue' allows adding even when out of stock
      const isPreorder = addBtn.dataset.isPreorder === 'true';
      const canAdd = this.currentVariant?.available ||
                     (isPreorder && this.currentVariant?.inventory_policy === 'continue');

      if (!canAdd || addBtn.disabled) {
        console.log('[RN Quick View] Cannot add - available:', this.currentVariant?.available,
                    'isPreorder:', isPreorder, 'policy:', this.currentVariant?.inventory_policy);
        return;
      }

      // Ensure variant ID is set correctly before submission
      const variantInput = form.querySelector('[data-rn-qv-variant-id]');
      if (variantInput && this.currentVariant) {
        variantInput.value = this.currentVariant.id;
      }

      // Set loading state
      addBtn.classList.add('is-loading');
      addBtn.disabled = true;

      const formData = new FormData(form);

      // Add pre-order line item properties if this is a backorder variant
      if (isPreorder && this.backorderVariants && this.backorderVariants.length > 0) {
        const variantId = this.currentVariant?.id?.toString();
        const backorderInfo = this.backorderVariants.find(v => v.id === variantId);

        if (backorderInfo && backorderInfo.shipment_note) {
          formData.set('properties[Note]', backorderInfo.shipment_note);
          console.log('[RN Quick View] Adding pre-order note:', backorderInfo.shipment_note);
        }
      }

      // Log what we're adding
      console.log('[RN Quick View] Adding to cart:', {
        variantId: formData.get('id'),
        quantity: formData.get('quantity'),
        variantTitle: this.currentVariant?.title,
        isPreorder: isPreorder,
        preorderNote: formData.get('properties[Note]')
      });

      try {
        // Collect sections to bundle (matches theme behavior)
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );

        // Add sections to form data if any
        if (sectionsToBundle.length > 0) {
          formData.set('sections', sectionsToBundle.join(','));
          formData.set('sections_url', `${window.Shopify?.routes?.root || '/'}variants/${this.currentVariant.id}`);
        }

        // Submit to cart
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        const responseJson = await response.json();

        if (!response.ok) {
          throw new Error(responseJson.description || 'Add to cart failed');
        }

        // Fetch full cart data (matches theme behavior)
        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
        const cartContent = await cartResponse.json();
        cartContent['sections'] = responseJson['sections'] || {};

        // Success state
        addBtn.classList.remove('is-loading');
        addBtn.classList.add('is-success');

        // Dispatch variant:add on form (matches theme's ProductForm)
        form.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
            cart: cartContent
          }
        }));

        // Dispatch cart:change on documentElement (matches theme behavior)
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent
          }
        }));

        console.log('[RN Quick View] ✓ Added to cart successfully', responseJson);

        // Reset after delay
        setTimeout(() => {
          addBtn.classList.remove('is-success');
          addBtn.disabled = false;

          // Close modal - cart drawer will open via cart:change event
          this.close();
        }, 800);

      } catch (error) {
        console.error('[RN Quick View] Add to cart error:', error);
        addBtn.classList.remove('is-loading');
        addBtn.disabled = false;

        // Dispatch error event (matches theme behavior)
        form.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));

        // Show error state briefly
        const btnText = addBtn.querySelector('.rn-quick-view__btn-text');
        if (btnText) {
          const originalText = btnText.textContent;
          btnText.textContent = 'Error - Try Again';
          setTimeout(() => {
            btnText.textContent = originalText;
          }, 2000);
        }
      }
    });
  }

  /**
   * Open modal
   */
  open() {
    if (!this.modal || this.isOpen) return;

    this.isOpen = true;
    document.body.style.overflow = 'hidden';

    // Trigger animations
    requestAnimationFrame(() => {
      this.backdrop?.classList.add('is-active');
      this.modal.classList.add('is-active');
    });

    // Focus first interactive element
    setTimeout(() => {
      const firstFocusable = this.modal.querySelector('button, [href], input');
      firstFocusable?.focus();
    }, 300);
  }

  /**
   * Close modal
   */
  close() {
    if (!this.modal || !this.isOpen) return;

    this.isOpen = false;
    document.body.style.overflow = '';

    this.modal.classList.remove('is-active');
    this.backdrop?.classList.remove('is-active');

    // Clean up after animation
    setTimeout(() => {
      document.removeEventListener('keydown', this.handleKeydown);
    }, 300);
  }

  /**
   * Keyboard handling
   */
  handleKeydown(e) {
    if (!this.isOpen) return;

    if (e.key === 'Escape') {
      this.close();
    }
  }

  /**
   * Touch handling for mobile swipe-to-close
   */
  handleTouchStart(e) {
    this.touchStartY = e.touches[0].clientY;
    this.touchStartTime = Date.now();
  }

  handleTouchMove(e) {
    if (!this.isOpen) return;

    const touchY = e.touches[0].clientY;
    const diff = touchY - this.touchStartY;
    const elapsed = Date.now() - this.touchStartTime;

    // Swipe down to close on mobile - require deliberate gesture:
    // - At least 180px swipe distance
    // - AND either: slow drag (>300ms) OR very fast intentional swipe (>250px in <200ms)
    const isSlowDrag = diff > 180 && elapsed > 300;
    const isFastSwipe = diff > 250 && elapsed < 200;

    if ((isSlowDrag || isFastSwipe) && window.innerWidth <= 768) {
      this.close();
    }
  }

  /**
   * Format money helper - matches Shopify's money format with space after $
   */
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return '$ ' + amount;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.rnQuickView = new RNQuickView();
  });
} else {
  window.rnQuickView = new RNQuickView();
}

// Also export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = RNQuickView;
}
